/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tabs,
  Tab,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Functions as FunctionsIcon,
  Code as CodeIcon,
  ContentCopy as CopyIcon,
  LibraryBooks as TemplateIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { ConfirmDialog, PageHeader } from '../common';
import {
  modelLabFeatureSetApi,
  modelLabDatasetApi,
  modelLabDatasetVersionApi,
} from '../../services/api';
import type {
  ModelLabFeatureSet,
  ModelLabFeatureSetCreate,
  ModelLabDataset,
  ModelLabDatasetVersion,
  FeaturePreview,
} from '../../types';

// ── Custom Feature types ──
type CustomFeatureType = 'preset' | 'expression' | 'python';

interface CustomFeature {
  id: string; // client-side unique key
  name: string;
  type: CustomFeatureType;
  // Preset fields
  transform?: string;
  source_column?: string;
  params?: Record<string, number>;
  // Expression fields
  formula?: string;
  // Python fields
  code?: string;
}

const PRESET_TRANSFORMS: { value: string; label: string; description: string; params: { key: string; label: string; default: number }[] }[] = [
  { value: 'sma', label: 'Simple Moving Average (SMA)', description: 'Mean over a rolling window', params: [{ key: 'window', label: 'Window', default: 20 }] },
  { value: 'ema', label: 'Exponential Moving Average (EMA)', description: 'Weighted mean favoring recent values', params: [{ key: 'span', label: 'Span', default: 20 }] },
  { value: 'rsi', label: 'RSI', description: 'Relative Strength Index (0-100)', params: [{ key: 'period', label: 'Period', default: 14 }] },
  { value: 'macd', label: 'MACD', description: 'Moving Average Convergence Divergence', params: [{ key: 'fast', label: 'Fast', default: 12 }, { key: 'slow', label: 'Slow', default: 26 }, { key: 'signal', label: 'Signal', default: 9 }] },
  { value: 'pct_change', label: '% Change', description: 'Percentage change over N periods', params: [{ key: 'periods', label: 'Periods', default: 1 }] },
  { value: 'lag', label: 'Lag', description: 'Shift column by N rows', params: [{ key: 'periods', label: 'Periods', default: 1 }] },
  { value: 'log', label: 'Log', description: 'Natural logarithm', params: [] },
  { value: 'diff', label: 'Difference', description: 'First difference (row - previous row)', params: [{ key: 'periods', label: 'Periods', default: 1 }] },
  { value: 'rolling_std', label: 'Rolling Std Dev', description: 'Standard deviation over a rolling window', params: [{ key: 'window', label: 'Window', default: 20 }] },
  { value: 'cumsum', label: 'Cumulative Sum', description: 'Running cumulative sum', params: [] },
  { value: 'ratio', label: 'Ratio (A / B)', description: 'Divide source by another column', params: [] },
  { value: 'zscore', label: 'Z-Score', description: 'Rolling z-score normalization', params: [{ key: 'window', label: 'Window', default: 20 }] },
  { value: 'bollinger_upper', label: 'Bollinger Upper', description: 'SMA + N standard deviations', params: [{ key: 'window', label: 'Window', default: 20 }, { key: 'std', label: 'Std Devs', default: 2 }] },
  { value: 'bollinger_lower', label: 'Bollinger Lower', description: 'SMA − N standard deviations', params: [{ key: 'window', label: 'Window', default: 20 }, { key: 'std', label: 'Std Devs', default: 2 }] },
];

const EMPTY_CUSTOM_FEATURE: CustomFeature = {
  id: '',
  name: '',
  type: 'preset',
  transform: 'sma',
  source_column: '',
  params: {},
  formula: '',
  code: 'def compute(df):\n    # Return a pandas Series\n    return df["close"].pct_change()',
};

// ── Feature Templates ──
interface FeatureTemplate {
  category: string;
  label: string;
  description: string;
  features: Omit<CustomFeature, 'id'>[];
}

const FEATURE_TEMPLATES: FeatureTemplate[] = [
  // ── Future Price / Target Features ──
  {
    category: 'Future Price',
    label: 'Future Close (1 bar)',
    description: 'The closing price 1 bar ahead — ideal as a regression target',
    features: [{ name: 'future_close_1', type: 'python', code: 'def compute(df):\n    return df["close"].shift(-1)' }],
  },
  {
    category: 'Future Price',
    label: 'Future Close (5 bars)',
    description: 'The closing price 5 bars ahead',
    features: [{ name: 'future_close_5', type: 'python', code: 'def compute(df):\n    return df["close"].shift(-5)' }],
  },
  {
    category: 'Future Price',
    label: 'Future Return % (1 bar)',
    description: 'Percentage return 1 bar ahead — common regression target',
    features: [{ name: 'future_return_1', type: 'python', code: 'def compute(df):\n    return (df["close"].shift(-1) - df["close"]) / df["close"] * 100' }],
  },
  {
    category: 'Future Price',
    label: 'Future Return % (5 bars)',
    description: 'Percentage return 5 bars ahead',
    features: [{ name: 'future_return_5', type: 'python', code: 'def compute(df):\n    return (df["close"].shift(-5) - df["close"]) / df["close"] * 100' }],
  },
  {
    category: 'Future Price',
    label: 'Future Direction (1 bar)',
    description: 'Binary 1/0 — whether price goes up next bar. Classification target',
    features: [{ name: 'future_direction_1', type: 'python', code: 'def compute(df):\n    return (df["close"].shift(-1) > df["close"]).astype(int)' }],
  },
  {
    category: 'Future Price',
    label: 'Future Direction (5 bars)',
    description: 'Binary 1/0 — whether price is higher 5 bars later',
    features: [{ name: 'future_direction_5', type: 'python', code: 'def compute(df):\n    return (df["close"].shift(-5) > df["close"]).astype(int)' }],
  },
  {
    category: 'Future Price',
    label: 'Future High-Low Range (1 bar)',
    description: 'The high-low range of the next bar — volatility target',
    features: [{ name: 'future_range_1', type: 'python', code: 'def compute(df):\n    return (df["high"].shift(-1) - df["low"].shift(-1))' }],
  },
  {
    category: 'Future Price',
    label: 'Future Max Drawdown (5 bars)',
    description: 'Worst drawdown (from close) over the next 5 bars',
    features: [{ name: 'future_max_dd_5', type: 'python', code: 'def compute(df):\n    import numpy as np\n    result = []\n    closes = df["close"].values\n    lows = df["low"].values\n    for i in range(len(df)):\n        future = lows[i+1:i+6] if i+1 < len(df) else []\n        dd = (min(future) - closes[i]) / closes[i] * 100 if len(future) > 0 else np.nan\n        result.append(dd)\n    return pd.Series(result, index=df.index)' }],
  },

  // ── Technical Indicators ──
  {
    category: 'Technical',
    label: 'Moving Average Crossover',
    description: 'SMA 10 & SMA 50 + crossover signal (1 when fast > slow)',
    features: [
      { name: 'sma_10', type: 'preset', transform: 'sma', source_column: 'close', params: { window: 10 } },
      { name: 'sma_50', type: 'preset', transform: 'sma', source_column: 'close', params: { window: 50 } },
      { name: 'ma_crossover', type: 'python', code: 'def compute(df):\n    sma10 = df["close"].rolling(10).mean()\n    sma50 = df["close"].rolling(50).mean()\n    return (sma10 > sma50).astype(int)' },
    ],
  },
  {
    category: 'Technical',
    label: 'RSI (14)',
    description: 'Relative Strength Index with 14-period lookback',
    features: [{ name: 'rsi_14', type: 'preset', transform: 'rsi', source_column: 'close', params: { period: 14 } }],
  },
  {
    category: 'Technical',
    label: 'MACD + Signal + Histogram',
    description: 'Full MACD set: line, signal line, and histogram divergence',
    features: [
      { name: 'macd_line', type: 'python', code: 'def compute(df):\n    return df["close"].ewm(span=12).mean() - df["close"].ewm(span=26).mean()' },
      { name: 'macd_signal', type: 'python', code: 'def compute(df):\n    macd = df["close"].ewm(span=12).mean() - df["close"].ewm(span=26).mean()\n    return macd.ewm(span=9).mean()' },
      { name: 'macd_histogram', type: 'python', code: 'def compute(df):\n    macd = df["close"].ewm(span=12).mean() - df["close"].ewm(span=26).mean()\n    signal = macd.ewm(span=9).mean()\n    return macd - signal' },
    ],
  },
  {
    category: 'Technical',
    label: 'Bollinger Bands (20, 2σ)',
    description: 'Upper band, lower band, and band width',
    features: [
      { name: 'bb_upper', type: 'preset', transform: 'bollinger_upper', source_column: 'close', params: { window: 20, std: 2 } },
      { name: 'bb_lower', type: 'preset', transform: 'bollinger_lower', source_column: 'close', params: { window: 20, std: 2 } },
      { name: 'bb_width', type: 'python', code: 'def compute(df):\n    sma = df["close"].rolling(20).mean()\n    std = df["close"].rolling(20).std()\n    return (4 * std) / sma * 100' },
    ],
  },
  {
    category: 'Technical',
    label: 'ATR (14)',
    description: 'Average True Range — measures volatility',
    features: [{ name: 'atr_14', type: 'python', code: 'def compute(df):\n    import numpy as np\n    h_l = df["high"] - df["low"]\n    h_pc = (df["high"] - df["close"].shift(1)).abs()\n    l_pc = (df["low"] - df["close"].shift(1)).abs()\n    tr = pd.concat([h_l, h_pc, l_pc], axis=1).max(axis=1)\n    return tr.rolling(14).mean()' }],
  },
  {
    category: 'Technical',
    label: 'Stochastic %K / %D (14, 3)',
    description: 'Stochastic oscillator — momentum indicator',
    features: [
      { name: 'stoch_k', type: 'python', code: 'def compute(df):\n    low14 = df["low"].rolling(14).min()\n    high14 = df["high"].rolling(14).max()\n    return (df["close"] - low14) / (high14 - low14) * 100' },
      { name: 'stoch_d', type: 'python', code: 'def compute(df):\n    low14 = df["low"].rolling(14).min()\n    high14 = df["high"].rolling(14).max()\n    k = (df["close"] - low14) / (high14 - low14) * 100\n    return k.rolling(3).mean()' },
    ],
  },

  // ── Price Action ──
  {
    category: 'Price Action',
    label: 'Candlestick Body & Shadows',
    description: 'Body size, upper shadow, lower shadow, body ratio',
    features: [
      { name: 'candle_body', type: 'expression', formula: 'close - open' },
      { name: 'candle_body_abs', type: 'expression', formula: 'abs(close - open)' },
      { name: 'upper_shadow', type: 'python', code: 'def compute(df):\n    return df["high"] - df[["open", "close"]].max(axis=1)' },
      { name: 'lower_shadow', type: 'python', code: 'def compute(df):\n    return df[["open", "close"]].min(axis=1) - df["low"]' },
      { name: 'body_ratio', type: 'python', code: 'def compute(df):\n    body = (df["close"] - df["open"]).abs()\n    total = df["high"] - df["low"]\n    return body / total.replace(0, float("nan"))' },
    ],
  },
  {
    category: 'Price Action',
    label: 'Gap (Open vs Previous Close)',
    description: 'Gap between current open and previous close',
    features: [
      { name: 'gap', type: 'python', code: 'def compute(df):\n    return df["open"] - df["close"].shift(1)' },
      { name: 'gap_pct', type: 'python', code: 'def compute(df):\n    return (df["open"] - df["close"].shift(1)) / df["close"].shift(1) * 100' },
    ],
  },
  {
    category: 'Price Action',
    label: 'Support/Resistance Distance',
    description: 'Distance from close to recent rolling high and low',
    features: [
      { name: 'dist_to_high_20', type: 'python', code: 'def compute(df):\n    rh = df["high"].rolling(20).max()\n    return (df["close"] - rh) / rh * 100' },
      { name: 'dist_to_low_20', type: 'python', code: 'def compute(df):\n    rl = df["low"].rolling(20).min()\n    return (df["close"] - rl) / rl * 100' },
    ],
  },

  // ── Volume ──
  {
    category: 'Volume',
    label: 'Volume Indicators',
    description: 'Volume SMA, volume ratio, and on-balance volume (OBV)',
    features: [
      { name: 'vol_sma_20', type: 'preset', transform: 'sma', source_column: 'volume', params: { window: 20 } },
      { name: 'vol_ratio', type: 'python', code: 'def compute(df):\n    return df["volume"] / df["volume"].rolling(20).mean()' },
      { name: 'obv', type: 'python', code: 'def compute(df):\n    import numpy as np\n    sign = np.sign(df["close"].diff())\n    return (sign * df["volume"]).cumsum()' },
    ],
  },
  {
    category: 'Volume',
    label: 'VWAP (Volume-Weighted Avg Price)',
    description: 'Cumulative VWAP — common intraday benchmark',
    features: [{ name: 'vwap', type: 'python', code: 'def compute(df):\n    typical = (df["high"] + df["low"] + df["close"]) / 3\n    return (typical * df["volume"]).cumsum() / df["volume"].cumsum()' }],
  },

  // ── Volatility ──
  {
    category: 'Volatility',
    label: 'Rolling Volatility (20, 50)',
    description: 'Annualized volatility based on log returns',
    features: [
      { name: 'volatility_20', type: 'python', code: 'def compute(df):\n    import numpy as np\n    lr = np.log(df["close"] / df["close"].shift(1))\n    return lr.rolling(20).std() * np.sqrt(252)' },
      { name: 'volatility_50', type: 'python', code: 'def compute(df):\n    import numpy as np\n    lr = np.log(df["close"] / df["close"].shift(1))\n    return lr.rolling(50).std() * np.sqrt(252)' },
    ],
  },
  {
    category: 'Volatility',
    label: 'Garman-Klass Volatility',
    description: 'More efficient volatility estimator using OHLC data',
    features: [{ name: 'gk_vol', type: 'python', code: 'def compute(df):\n    import numpy as np\n    a = 0.5 * np.log(df["high"] / df["low"]) ** 2\n    b = (2 * np.log(2) - 1) * np.log(df["close"] / df["open"]) ** 2\n    return np.sqrt((a - b).rolling(20).mean() * 252)' }],
  },

  // ── Returns & Momentum ──
  {
    category: 'Momentum',
    label: 'Multi-Period Returns',
    description: 'Log returns over 1, 5, 10, 20 periods',
    features: [
      { name: 'return_1', type: 'python', code: 'def compute(df):\n    import numpy as np\n    return np.log(df["close"] / df["close"].shift(1))' },
      { name: 'return_5', type: 'python', code: 'def compute(df):\n    import numpy as np\n    return np.log(df["close"] / df["close"].shift(5))' },
      { name: 'return_10', type: 'python', code: 'def compute(df):\n    import numpy as np\n    return np.log(df["close"] / df["close"].shift(10))' },
      { name: 'return_20', type: 'python', code: 'def compute(df):\n    import numpy as np\n    return np.log(df["close"] / df["close"].shift(20))' },
    ],
  },
  {
    category: 'Momentum',
    label: 'Rate of Change (ROC)',
    description: 'Percentage change over 12 periods — classic momentum',
    features: [{ name: 'roc_12', type: 'preset', transform: 'pct_change', source_column: 'close', params: { periods: 12 } }],
  },
  {
    category: 'Momentum',
    label: 'Momentum Score (Multi-Timeframe)',
    description: 'Composite score: average z-score of returns at 5, 10, 20 periods',
    features: [{ name: 'momentum_score', type: 'python', code: 'def compute(df):\n    import numpy as np\n    def zscore(s, w=50):\n        return (s - s.rolling(w).mean()) / s.rolling(w).std()\n    r5 = np.log(df["close"] / df["close"].shift(5))\n    r10 = np.log(df["close"] / df["close"].shift(10))\n    r20 = np.log(df["close"] / df["close"].shift(20))\n    return (zscore(r5) + zscore(r10) + zscore(r20)) / 3' }],
  },

  // ── Time / Calendar ──
  {
    category: 'Time',
    label: 'Calendar Features',
    description: 'Day of week, month, quarter, is-month-end — from datetime index',
    features: [
      { name: 'day_of_week', type: 'python', code: 'def compute(df):\n    return pd.to_datetime(df.index).dayofweek' },
      { name: 'month', type: 'python', code: 'def compute(df):\n    return pd.to_datetime(df.index).month' },
      { name: 'quarter', type: 'python', code: 'def compute(df):\n    return pd.to_datetime(df.index).quarter' },
      { name: 'is_month_end', type: 'python', code: 'def compute(df):\n    return pd.to_datetime(df.index).is_month_end.astype(int)' },
    ],
  },
  {
    category: 'Time',
    label: 'Bars Since High / Low',
    description: 'How many bars since the rolling 20-period high or low',
    features: [
      { name: 'bars_since_high', type: 'python', code: 'def compute(df):\n    import numpy as np\n    result = []\n    for i in range(len(df)):\n        window = df["high"].iloc[max(0,i-19):i+1]\n        result.append(len(window) - 1 - window.values.argmax())\n    return pd.Series(result, index=df.index)' },
      { name: 'bars_since_low', type: 'python', code: 'def compute(df):\n    import numpy as np\n    result = []\n    for i in range(len(df)):\n        window = df["low"].iloc[max(0,i-19):i+1]\n        result.append(len(window) - 1 - window.values.argmin())\n    return pd.Series(result, index=df.index)' },
    ],
  },

  // ── Statistical ──
  {
    category: 'Statistical',
    label: 'Skew & Kurtosis (20-period)',
    description: 'Rolling skewness and kurtosis of returns — tail risk signals',
    features: [
      { name: 'return_skew_20', type: 'python', code: 'def compute(df):\n    return df["close"].pct_change().rolling(20).skew()' },
      { name: 'return_kurt_20', type: 'python', code: 'def compute(df):\n    return df["close"].pct_change().rolling(20).kurt()' },
    ],
  },
  {
    category: 'Statistical',
    label: 'Autocorrelation (lag 1 & 5)',
    description: 'Rolling autocorrelation of returns — mean-reversion signal',
    features: [
      { name: 'autocorr_1', type: 'python', code: 'def compute(df):\n    r = df["close"].pct_change()\n    return r.rolling(20).apply(lambda x: x.autocorr(lag=1), raw=False)' },
      { name: 'autocorr_5', type: 'python', code: 'def compute(df):\n    r = df["close"].pct_change()\n    return r.rolling(20).apply(lambda x: x.autocorr(lag=5), raw=False)' },
    ],
  },
];

const TEMPLATE_CATEGORIES = [...new Set(FEATURE_TEMPLATES.map((t) => t.category))];

let _cfId = 0;
const nextCfId = () => `cf_${++_cfId}_${Date.now()}`;

interface FeatureSetForm {
  name: string;
  description: string;
  dataset: number | null;
  feature_columns: string[];
  target_column: string;
  preprocessing: Record<string, string>;
  custom_features: CustomFeature[];
  tags: string[];
}

const EMPTY_FORM: FeatureSetForm = {
  name: '',
  description: '',
  dataset: null,
  feature_columns: [],
  target_column: '',
  preprocessing: {},
  custom_features: [],
  tags: [],
};

const PREPROCESSING_OPTIONS: { value: string; label: string; description: string }[] = [
  { value: 'none', label: 'None', description: 'No transformation applied' },
  { value: 'standard_scaler', label: 'Standard Scaler', description: 'Zero mean, unit variance (z-score)' },
  { value: 'min_max_scaler', label: 'Min-Max Scaler', description: 'Scale to [0, 1] range' },
  { value: 'robust_scaler', label: 'Robust Scaler', description: 'Scale using median & IQR (outlier robust)' },
  { value: 'log_transform', label: 'Log Transform', description: 'Natural log to reduce skewness' },
  { value: 'one_hot_encode', label: 'One-Hot Encode', description: 'Convert categorical to binary columns' },
  { value: 'label_encode', label: 'Label Encode', description: 'Convert categorical to integers' },
  { value: 'fill_mean', label: 'Fill Mean', description: 'Replace nulls with column mean' },
  { value: 'fill_median', label: 'Fill Median', description: 'Replace nulls with column median' },
  { value: 'fill_zero', label: 'Fill Zero', description: 'Replace nulls with 0' },
  { value: 'drop_na', label: 'Drop NA', description: 'Remove rows with null values' },
];

export default function ModelLabFeaturesPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FeatureSetForm>(EMPTY_FORM);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [tab, setTab] = useState(0);
  const [featureSetToDelete, setFeatureSetToDelete] = useState<ModelLabFeatureSet | null>(null);
  const [search, setSearch] = useState('');
  // Custom feature builder state
  const [cfEditing, setCfEditing] = useState<CustomFeature | null>(null);
  const [cfTab, setCfTab] = useState<CustomFeatureType>('preset');
  const [pageTab, setPageTab] = useState(0); // 0 = list, 1 = feature builder
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateCat, setTemplateCat] = useState<string>(TEMPLATE_CATEGORIES[0]);

  // Queries
  const { data: featureSets, isLoading } = useQuery({
    queryKey: ['modelLabFeatureSets', search],
    queryFn: () => modelLabFeatureSetApi.list({ search: search || undefined }),
  });

  const { data: datasets } = useQuery({
    queryKey: ['modelLabDatasets'],
    queryFn: () => modelLabDatasetApi.list(),
  });

  const { data: selectedDatasetVersions, isLoading: versionsLoading } = useQuery({
    queryKey: ['modelLabDatasetVersions', form.dataset],
    queryFn: () =>
      form.dataset ? modelLabDatasetApi.getVersions(form.dataset) : Promise.resolve([]),
    enabled: !!form.dataset,
  });

  const { data: preview, isLoading: previewLoading } = useQuery({
    queryKey: ['featureSetPreview', previewId],
    queryFn: () => (previewId ? modelLabFeatureSetApi.preview(previewId) : Promise.resolve(null)),
    enabled: !!previewId,
  });

  // The currently selected dataset object (for checking versions_count, schema)
  const selectedDataset = useMemo(() => {
    if (!form.dataset || !datasets?.results) return null;
    return datasets.results.find((d) => d.id === form.dataset) ?? null;
  }, [form.dataset, datasets]);

  // Available columns from latest version records, falling back to version/dataset schema
  const datasetColumns = useMemo(() => {
    if (!form.dataset) return [];
    // Try records first
    if (selectedDatasetVersions?.length) {
      const latest = selectedDatasetVersions[0];
      if (latest?.records?.length) {
        return Object.keys(latest.records[0]);
      }
      // Fallback: version schema columns
      const vCols = latest?.schema?.columns;
      if (Array.isArray(vCols) && vCols.length) return vCols as string[];
    }
    // Fallback: dataset-level schema columns
    const dCols = selectedDataset?.schema?.columns;
    if (Array.isArray(dCols) && dCols.length) return dCols as string[];
    return [];
  }, [selectedDatasetVersions, form.dataset, selectedDataset]);

  // Merge dataset columns + custom feature names so they appear in feature/target dropdowns
  const availableColumns = useMemo(() => {
    const cfNames = form.custom_features.map((cf) => cf.name).filter(Boolean);
    const all = [...datasetColumns, ...cfNames.filter((n) => !datasetColumns.includes(n))];
    return all;
  }, [datasetColumns, form.custom_features]);

  // Detect column type by scanning multiple records (handles nulls in first records)
  const detectColumnType = (col: string): 'number' | 'string' | 'unknown' => {
    const records = selectedDatasetVersions?.[0]?.records;
    if (!records?.length) return 'unknown';
    for (let i = 0; i < Math.min(records.length, 20); i++) {
      const val = records[i]?.[col];
      if (val == null) continue;
      if (typeof val === 'number') return 'number';
      if (typeof val === 'string') return 'string';
    }
    return 'unknown';
  };

  // ── Custom Feature helpers ──
  const startAddCustomFeature = useCallback(() => {
    const newCf: CustomFeature = { ...EMPTY_CUSTOM_FEATURE, id: nextCfId(), type: cfTab };
    const preset = PRESET_TRANSFORMS[0];
    if (cfTab === 'preset') {
      const defaults: Record<string, number> = {};
      preset.params.forEach((p) => { defaults[p.key] = p.default; });
      newCf.transform = preset.value;
      newCf.params = defaults;
    }
    setCfEditing(newCf);
  }, [cfTab]);

  const saveCustomFeature = useCallback(() => {
    if (!cfEditing || !cfEditing.name.trim()) return;
    setForm((prev) => {
      const exists = prev.custom_features.findIndex((c) => c.id === cfEditing.id);
      const updated = [...prev.custom_features];
      if (exists >= 0) {
        updated[exists] = cfEditing;
      } else {
        updated.push(cfEditing);
      }
      return { ...prev, custom_features: updated };
    });
    setCfEditing(null);
  }, [cfEditing]);

  const removeCustomFeature = useCallback((id: string) => {
    setForm((prev) => ({
      ...prev,
      custom_features: prev.custom_features.filter((c) => c.id !== id),
    }));
  }, []);

  const duplicateCustomFeature = useCallback((cf: CustomFeature) => {
    const dup: CustomFeature = { ...cf, id: nextCfId(), name: `${cf.name}_copy` };
    setForm((prev) => ({ ...prev, custom_features: [...prev.custom_features, dup] }));
  }, []);

  const updateCfEditing = useCallback((patch: Partial<CustomFeature>) => {
    setCfEditing((prev) => prev ? { ...prev, ...patch } : prev);
  }, []);

  const applyTemplate = useCallback((tpl: FeatureTemplate) => {
    const newFeatures = tpl.features.map((f) => ({ ...f, id: nextCfId() }));
    setForm((prev) => ({
      ...prev,
      custom_features: [...prev.custom_features, ...newFeatures],
    }));
    toast.success(`Added ${newFeatures.length} feature${newFeatures.length > 1 ? 's' : ''} from "${tpl.label}"`);
  }, []);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: ModelLabFeatureSetCreate) => modelLabFeatureSetApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modelLabFeatureSets'] });
      toast.success('Feature set created');
      closeDialog();
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Create failed'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ModelLabFeatureSetCreate> }) =>
      modelLabFeatureSetApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modelLabFeatureSets'] });
      toast.success('Feature set updated');
      closeDialog();
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Update failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => modelLabFeatureSetApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modelLabFeatureSets'] });
      toast.success('Feature set deleted');
    },
  });

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (fs: ModelLabFeatureSet) => {
    setEditingId(fs.id);
    const def = fs.definition || {};
    setForm({
      name: fs.name,
      description: fs.description,
      dataset: fs.dataset,
      feature_columns: def.feature_columns || [],
      target_column: def.target_column || '',
      preprocessing: def.preprocessing || {},
      custom_features: (def.custom_features || []).map((cf: any, i: number) => ({
        ...cf,
        id: cf.id || nextCfId(),
      })),
      tags: fs.tags || [],
    });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    const payload: ModelLabFeatureSetCreate = {
      name: form.name,
      description: form.description,
      dataset: form.dataset,
      definition: {
        feature_columns: form.feature_columns,
        target_column: form.target_column,
        preprocessing: form.preprocessing,
        custom_features: form.custom_features.map(({ id, ...rest }) => rest),
      },
      tags: form.tags,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const items = featureSets?.results || [];

  return (
    <Box>
      <PageHeader
        title="Feature Engineering"
        breadcrumbs={[
          { label: 'Automation', to: '/ai' },
          { label: 'Model Lab', to: '/ai/ml' },
          { label: 'Feature Sets' },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => queryClient.invalidateQueries({ queryKey: ['modelLabFeatureSets'] })}
              size="small"
            >
              Refresh
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New Feature Set
            </Button>
          </Stack>
        }
      />

      <Tabs value={pageTab} onChange={(_, v) => setPageTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Feature Sets" />
        <Tab label="Feature Builder" icon={<FunctionsIcon />} iconPosition="start" />
      </Tabs>

      {pageTab === 0 && (
      <Stack spacing={2}>
        <Alert severity="info">
          Feature sets define which columns from a dataset are used as inputs (features)
          and which column is the prediction target. You can also configure per-feature
          preprocessing transformations and custom derived features.
        </Alert>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Alert severity="info">
          No feature sets yet. Create one to define features for training.
        </Alert>
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Name</TableCell>
                <TableCell>Dataset</TableCell>
                <TableCell>Features</TableCell>
                <TableCell>Custom</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((fs) => {
                const def = fs.definition || {};
                const cols = def.feature_columns || [];
                const customCount = (def.custom_features || []).length;
                const isExpanded = expandedId === fs.id;
                return (
                  <React.Fragment key={fs.id}>
                    <TableRow hover>
                      <TableCell sx={{ width: 40 }}>
                        <IconButton
                          size="small"
                          onClick={() => setExpandedId(isExpanded ? null : fs.id)}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={600}>{fs.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fs.description?.slice(0, 60)}
                        </Typography>
                      </TableCell>
                      <TableCell>{fs.dataset_name || '—'}</TableCell>
                      <TableCell>
                        <Chip label={`${cols.length} features`} size="small" />
                      </TableCell>
                      <TableCell>
                        {customCount > 0 ? (
                          <Chip label={`${customCount} custom`} size="small" color="secondary" variant="outlined" />
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        {def.target_column ? (
                          <Chip label={def.target_column} size="small" color="primary" variant="outlined" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        {(fs.tags || []).map((t: string) => (
                          <Chip key={t} label={t} size="small" sx={{ mr: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Preview">
                          <IconButton
                            size="small"
                            onClick={() => setPreviewId(previewId === fs.id ? null : fs.id)}
                          >
                            <PreviewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(fs)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setFeatureSetToDelete(fs)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Row: Feature details */}
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded}>
                          <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Feature Columns
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                              {cols.map((c: string) => (
                                <Chip key={c} label={c} size="small" variant="outlined" />
                              ))}
                              {cols.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  No feature columns defined
                                </Typography>
                              )}
                            </Box>
                            {def.preprocessing &&
                              Object.keys(def.preprocessing).length > 0 && (
                                <>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Preprocessing
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {Object.entries(def.preprocessing).map(([col, method]) => (
                                      <Chip
                                        key={col}
                                        label={`${col}: ${method}`}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                      />
                                    ))}
                                  </Box>
                                </>
                              )}
                            {def.custom_features && def.custom_features.length > 0 && (
                              <>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                                  Custom Features
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {def.custom_features.map((cf: any, i: number) => (
                                    <Chip
                                      key={i}
                                      label={`${cf.name} (${cf.type})`}
                                      size="small"
                                      color={cf.type === 'preset' ? 'primary' : cf.type === 'expression' ? 'secondary' : 'warning'}
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              </>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Preview Panel */}
      {previewId && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Feature Set Preview
            </Typography>
            {previewLoading ? (
              <CircularProgress size={24} />
            ) : preview ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Showing {preview.rows.length} of {preview.total_rows} rows |{' '}
                  {preview.valid_feature_columns.length} valid features
                  {preview.target_column && ` | Target: ${preview.target_column}`}
                </Typography>
                <Box sx={{ overflowX: 'auto', maxHeight: 400 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {preview.columns.map((col) => (
                          <TableCell key={col}>
                            <Typography variant="caption" fontWeight={700}>
                              {col}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.rows.map((row, i) => (
                        <TableRow key={i}>
                          {preview.columns.map((col) => (
                            <TableCell key={col}>
                              <Typography variant="caption">
                                {row[col] != null ? String(row[col]) : '—'}
                              </Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </>
            ) : (
              <Alert severity="warning">No preview data available</Alert>
            )}
          </CardContent>
        </Card>
      )}
      </Stack>
      )}

      {/* ── Feature Builder Tab ── */}
      {pageTab === 1 && (
        <FeatureBuilderTab
          datasets={datasets?.results || []}
          getVersions={modelLabDatasetApi.getVersions}
        />
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit Feature Set' : 'Create Feature Set'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* ── Section: Identity ── */}
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
              Identity
            </Typography>
            <TextField
              label="Name"
              placeholder="e.g. forex-price-features, sentiment-indicators"
              helperText="A unique name to identify this feature set"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Description"
              placeholder="Describe what this feature set captures and its intended use"
              helperText="Helps teammates understand the purpose of this feature set"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />

            <Divider />

            {/* ── Section: Data Source ── */}
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
              Data Source & Column Selection
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Dataset</InputLabel>
              <Select
                value={form.dataset || ''}
                label="Dataset"
                onChange={(e) =>
                  setForm({
                    ...form,
                    dataset: e.target.value ? Number(e.target.value) : null,
                    feature_columns: [],
                    target_column: '',
                  })
                }
              >
                <MenuItem value="">None</MenuItem>
                {(datasets?.results || []).map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!form.dataset && (
              <Alert severity="warning" variant="outlined">
                Select a dataset to reveal available columns for feature and target selection.
              </Alert>
            )}
            {form.dataset && versionsLoading && (
              <Alert severity="info" variant="outlined">
                Loading dataset versions…
              </Alert>
            )}
            {form.dataset && !versionsLoading && selectedDatasetVersions && selectedDatasetVersions.length === 0 && (
              <Alert severity="error" variant="outlined">
                This dataset has no versions yet. Go to the <strong>Datasets</strong> page and add a
                version with records before creating a feature set.
              </Alert>
            )}
            {form.dataset && !versionsLoading && selectedDatasetVersions && selectedDatasetVersions.length > 0 && availableColumns.length === 0 && (
              <Alert severity="warning" variant="outlined">
                The latest version of this dataset has no records or column information.
                Add data to the dataset version or update the schema to continue.
              </Alert>
            )}

            {form.dataset && !versionsLoading && (
              <>
                {availableColumns.length > 0 && (
                  <Alert severity="success" variant="outlined">
                    {availableColumns.length} columns detected from the latest dataset version.
                    Select from the list or type custom column names.
                  </Alert>
                )}
                <Autocomplete
                  multiple
                  freeSolo
                  options={availableColumns.filter((c) => c !== form.target_column)}
                  value={form.feature_columns}
                  onChange={(_, val) => setForm({ ...form, feature_columns: val as string[] })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Feature Columns"
                      placeholder={availableColumns.length > 0 ? 'Select or type features' : 'Type feature column names and press Enter'}
                      helperText={
                        availableColumns.length > 0
                          ? `${form.feature_columns.length} of ${availableColumns.length} columns selected as input features`
                          : 'Type column names manually and press Enter to add'
                      }
                    />
                  )}
                />

                <Autocomplete
                  freeSolo
                  options={availableColumns.filter((c) => !form.feature_columns.includes(c))}
                  value={form.target_column || null}
                  onChange={(_, val) => setForm({ ...form, target_column: (val as string) || '' })}
                  onInputChange={(_, val, reason) => {
                    if (reason === 'input') setForm((prev) => ({ ...prev, target_column: val }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Target Column"
                      placeholder={availableColumns.length > 0 ? 'Select or type target' : 'Type target column name'}
                      helperText="The column your model will learn to predict"
                    />
                  )}
                />

                <Divider />

                {/* ── Section: Preprocessing ── */}
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
                  Preprocessing (per feature)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
                  Configure how each feature column is transformed before training.
                  Color-coded chips indicate detected column types.
                </Typography>
                {form.feature_columns.map((col) => (
                  <Box key={col} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title={
                      detectColumnType(col) === 'number'
                        ? 'Numeric column'
                        : detectColumnType(col) === 'string'
                          ? 'Text/categorical column'
                          : 'Unknown type'
                    }>
                      <Chip
                        label={col}
                        size="small"
                        variant="outlined"
                        color={
                          detectColumnType(col) === 'number'
                            ? 'primary'
                            : detectColumnType(col) === 'string'
                              ? 'secondary'
                              : 'default'
                        }
                        sx={{ minWidth: 100, justifyContent: 'flex-start' }}
                      />
                    </Tooltip>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value={form.preprocessing[col] || 'none'}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            preprocessing: {
                              ...form.preprocessing,
                              [col]: e.target.value,
                            },
                          })
                        }
                      >
                        {PREPROCESSING_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            <Box>
                              <Typography variant="body2">{opt.label}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {opt.description}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ))}
                {form.feature_columns.length === 0 && (
                  <Alert severity="info" variant="outlined">
                    Select feature columns above to configure preprocessing per column.
                  </Alert>
                )}
              </>
            )}

            <Divider />

            {/* ── Section: Custom Features ── */}
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
              Custom Features
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
              Derive new features from existing columns using preset transforms, math expressions, or Python code.
            </Typography>

            {/* List of existing custom features */}
            {form.custom_features.map((cf) => (
              <Paper key={cf.id} variant="outlined" sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={cf.type === 'preset' ? 'Preset' : cf.type === 'expression' ? 'Expr' : 'Python'}
                      size="small"
                      color={cf.type === 'preset' ? 'primary' : cf.type === 'expression' ? 'secondary' : 'warning'}
                      variant="outlined"
                    />
                    <Typography variant="subtitle2">{cf.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cf.type === 'preset' && `${cf.transform}(${cf.source_column || '?'}${cf.params && Object.keys(cf.params).length ? ', ' + Object.entries(cf.params).map(([k, v]) => `${k}=${v}`).join(', ') : ''})`}
                      {cf.type === 'expression' && (cf.formula || '—')}
                      {cf.type === 'python' && 'Python function'}
                    </Typography>
                  </Box>
                  <Box>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => { setCfEditing({ ...cf }); setCfTab(cf.type); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                      <IconButton size="small" onClick={() => duplicateCustomFeature(cf)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => removeCustomFeature(cf.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Paper>
            ))}

            {/* Add / Edit custom feature form */}
            {cfEditing ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle2">
                    {form.custom_features.some((c) => c.id === cfEditing.id) ? 'Edit' : 'New'} Custom Feature
                  </Typography>
                  <TextField
                    label="Feature Name"
                    placeholder="e.g. sma_close_20, price_spread, custom_signal"
                    helperText="The derived column name — must be unique within this feature set"
                    value={cfEditing.name}
                    onChange={(e) => updateCfEditing({ name: e.target.value })}
                    size="small"
                    fullWidth
                    required
                  />

                  <Tabs
                    value={cfTab}
                    onChange={(_, v) => { setCfTab(v); updateCfEditing({ type: v }); }}
                    variant="fullWidth"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Tab label="Preset" value="preset" icon={<FunctionsIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
                    <Tab label="Expression" value="expression" icon={<FunctionsIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
                    <Tab label="Python" value="python" icon={<CodeIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
                  </Tabs>

                  {/* ── Preset tab ── */}
                  {cfTab === 'preset' && (
                    <Stack spacing={2}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Transform</InputLabel>
                        <Select
                          value={cfEditing.transform || 'sma'}
                          label="Transform"
                          onChange={(e) => {
                            const t = PRESET_TRANSFORMS.find((p) => p.value === e.target.value);
                            const defaults: Record<string, number> = {};
                            t?.params.forEach((p) => { defaults[p.key] = p.default; });
                            updateCfEditing({ transform: e.target.value, params: defaults });
                          }}
                        >
                          {PRESET_TRANSFORMS.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                              <Box>
                                <Typography variant="body2">{t.label}</Typography>
                                <Typography variant="caption" color="text.secondary">{t.description}</Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Autocomplete
                        freeSolo
                        options={availableColumns}
                        value={cfEditing.source_column || ''}
                        onChange={(_, val) => updateCfEditing({ source_column: (val as string) || '' })}
                        onInputChange={(_, val, reason) => {
                          if (reason === 'input') updateCfEditing({ source_column: val });
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Source Column" placeholder="Select or type column" size="small" helperText="The column to apply the transform on" />
                        )}
                        size="small"
                      />
                      {(() => {
                        const t = PRESET_TRANSFORMS.find((p) => p.value === cfEditing.transform);
                        if (!t?.params.length) return null;
                        return (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {t.params.map((p) => (
                              <TextField
                                key={p.key}
                                label={p.label}
                                type="number"
                                size="small"
                                value={cfEditing.params?.[p.key] ?? p.default}
                                onChange={(e) =>
                                  updateCfEditing({
                                    params: { ...cfEditing.params, [p.key]: Number(e.target.value) || 0 },
                                  })
                                }
                                sx={{ width: 120 }}
                              />
                            ))}
                          </Box>
                        );
                      })()}
                      {cfEditing.transform === 'ratio' && (
                        <Autocomplete
                          freeSolo
                          options={availableColumns.filter((c) => c !== cfEditing.source_column)}
                          value={(cfEditing.params as any)?.denominator_column || ''}
                          onChange={(_, val) =>
                            updateCfEditing({ params: { ...cfEditing.params, denominator_column: val as any } })
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Denominator Column" placeholder="Column to divide by" size="small" />
                          )}
                          size="small"
                        />
                      )}
                    </Stack>
                  )}

                  {/* ── Expression tab ── */}
                  {cfTab === 'expression' && (
                    <Stack spacing={2}>
                      <TextField
                        label="Formula"
                        placeholder="e.g. close - open, high / low, (ask + bid) / 2"
                        helperText={
                          availableColumns.length > 0
                            ? `Available columns: ${availableColumns.slice(0, 8).join(', ')}${availableColumns.length > 8 ? '...' : ''}`
                            : 'Reference column names from your dataset'
                        }
                        value={cfEditing.formula || ''}
                        onChange={(e) => updateCfEditing({ formula: e.target.value })}
                        size="small"
                        fullWidth
                      />
                      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                        Supports arithmetic: <code>+</code> <code>-</code> <code>*</code> <code>/</code> and
                        functions like <code>abs()</code>, <code>log()</code>, <code>sqrt()</code>, <code>lag(col, n)</code>, <code>sma(col, n)</code>.
                      </Alert>
                    </Stack>
                  )}

                  {/* ── Python tab ── */}
                  {cfTab === 'python' && (
                    <Stack spacing={2}>
                      <TextField
                        label="Python Code"
                        placeholder={'def compute(df):\n    return df["close"].pct_change()'}
                        helperText="Define a function compute(df) that receives the full DataFrame and returns a pandas Series"
                        value={cfEditing.code || ''}
                        onChange={(e) => updateCfEditing({ code: e.target.value })}
                        multiline
                        rows={6}
                        fullWidth
                        slotProps={{
                          input: {
                            sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                          },
                        }}
                      />
                      <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                        Python features run in a sandboxed environment. Only <code>pandas</code>, <code>numpy</code>,
                        and <code>math</code> are available.
                      </Alert>
                    </Stack>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => setCfEditing(null)}>Cancel</Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={saveCustomFeature}
                      disabled={!cfEditing.name.trim() || (cfTab === 'preset' && !cfEditing.source_column) || (cfTab === 'expression' && !cfEditing.formula?.trim()) || (cfTab === 'python' && !cfEditing.code?.trim())}
                    >
                      {form.custom_features.some((c) => c.id === cfEditing.id) ? 'Update' : 'Add'}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            ) : (
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                size="small"
                onClick={startAddCustomFeature}
              >
                Add Custom Feature
              </Button>
            )}

            <Button
              startIcon={<TemplateIcon />}
              variant="outlined"
              size="small"
              color="secondary"
              onClick={() => setTemplateOpen(true)}
            >
              Browse Templates
            </Button>

            {form.custom_features.length === 0 && !cfEditing && (
              <Alert severity="info" variant="outlined">
                No custom features yet. Click &quot;Add Custom Feature&quot; to derive new columns
                using presets (SMA, RSI, MACD…), expressions, or Python code.
              </Alert>
            )}

            <Divider />

            {/* ── Section: Tags ── */}
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
              Organization
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={form.tags}
              onChange={(_, val) => setForm({ ...form, tags: val as string[] })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Press enter to add"
                  helperText="Optional labels for filtering and categorization"
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!form.name || createMutation.isPending || updateMutation.isPending}
          >
            {editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Template Picker Dialog ── */}
      <Dialog open={templateOpen} onClose={() => setTemplateOpen(false)} maxWidth="md" fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TemplateIcon /> Feature Templates
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click a template to add its features to your feature set. Templates marked with multiple features add them all at once.
          </Typography>
          <Tabs
            value={templateCat}
            onChange={(_, v) => setTemplateCat(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            {TEMPLATE_CATEGORIES.map((cat) => (
              <Tab key={cat} label={`${cat} (${FEATURE_TEMPLATES.filter((t) => t.category === cat).length})`} value={cat} />
            ))}
          </Tabs>
          <Stack spacing={1.5}>
            {FEATURE_TEMPLATES.filter((t) => t.category === templateCat).map((tpl, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => { applyTemplate(tpl); }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="subtitle2">{tpl.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{tpl.description}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                      {tpl.features.map((f, fi) => (
                        <Chip
                          key={fi}
                          label={f.name}
                          size="small"
                          variant="outlined"
                          color={f.type === 'preset' ? 'primary' : f.type === 'expression' ? 'secondary' : 'warning'}
                        />
                      ))}
                    </Box>
                  </Box>
                  <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); applyTemplate(tpl); }}>
                    + Add
                  </Button>
                </Box>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!featureSetToDelete}
        title="Delete Feature Set"
        message={`Are you sure you want to delete "${featureSetToDelete?.name}"? This cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={() => {
          if (featureSetToDelete) deleteMutation.mutate(featureSetToDelete.id);
          setFeatureSetToDelete(null);
        }}
        onCancel={() => setFeatureSetToDelete(null)}
      />
    </Box>
  );
}

// ────────────────────────────────────────────────────────────
// Feature Builder Tab — dedicated workspace for custom features
// ────────────────────────────────────────────────────────────
interface FeatureBuilderTabProps {
  datasets: ModelLabDataset[];
  getVersions: (id: number) => Promise<ModelLabDatasetVersion[]>;
}

function FeatureBuilderTab({ datasets, getVersions }: FeatureBuilderTabProps) {
  const [selectedDatasetId, setSelectedDatasetId] = useState<number | null>(null);
  const [features, setFeatures] = useState<CustomFeature[]>([]);
  const [editing, setEditing] = useState<CustomFeature | null>(null);
  const [editTab, setEditTab] = useState<CustomFeatureType>('preset');
  const [showTemplates, setShowTemplates] = useState(false);
  const [builderTemplateCat, setBuilderTemplateCat] = useState<string>(TEMPLATE_CATEGORIES[0]);

  const { data: versions } = useQuery({
    queryKey: ['builderVersions', selectedDatasetId],
    queryFn: () => selectedDatasetId ? getVersions(selectedDatasetId) : Promise.resolve([]),
    enabled: !!selectedDatasetId,
  });

  const columns = useMemo(() => {
    if (!versions?.length) return [];
    const latest = versions[0];
    if (latest?.records?.length) return Object.keys(latest.records[0]);
    const vCols = latest?.schema?.columns;
    if (Array.isArray(vCols)) return vCols as string[];
    return [];
  }, [versions]);

  const sampleRecords = useMemo(() => {
    if (!versions?.length) return [];
    return (versions[0]?.records || []).slice(0, 5);
  }, [versions]);

  const startNew = () => {
    const preset = PRESET_TRANSFORMS[0];
    const defaults: Record<string, number> = {};
    preset.params.forEach((p) => { defaults[p.key] = p.default; });
    setEditing({
      ...EMPTY_CUSTOM_FEATURE,
      id: nextCfId(),
      type: editTab,
      transform: preset.value,
      params: defaults,
    });
  };

  const save = () => {
    if (!editing || !editing.name.trim()) return;
    setFeatures((prev) => {
      const idx = prev.findIndex((c) => c.id === editing.id);
      if (idx >= 0) {
        const u = [...prev];
        u[idx] = editing;
        return u;
      }
      return [...prev, editing];
    });
    setEditing(null);
  };

  const remove = (id: string) => setFeatures((prev) => prev.filter((c) => c.id !== id));

  const duplicate = (cf: CustomFeature) => {
    setFeatures((prev) => [...prev, { ...cf, id: nextCfId(), name: `${cf.name}_copy` }]);
  };

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        The Feature Builder is a workspace for designing and previewing custom derived features.
        Select a dataset to see available columns and sample data, then build features using
        presets, expressions, or Python code.
      </Alert>

      {/* Dataset selector */}
      <FormControl fullWidth size="small">
        <InputLabel>Dataset</InputLabel>
        <Select
          value={selectedDatasetId || ''}
          label="Dataset"
          onChange={(e) => setSelectedDatasetId(e.target.value ? Number(e.target.value) : null)}
        >
          <MenuItem value="">Select a dataset</MenuItem>
          {datasets.map((d) => (
            <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Data preview */}
      {selectedDatasetId && columns.length > 0 && (
        <Card variant="outlined">
          <CardContent sx={{ pb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Data Preview — {columns.length} columns, {sampleRecords.length} sample rows
            </Typography>
            <Box sx={{ overflowX: 'auto', maxHeight: 250 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col}>
                        <Typography variant="caption" fontWeight={700}>{col}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sampleRecords.map((row, i) => (
                    <TableRow key={i}>
                      {columns.map((col) => (
                        <TableCell key={col}>
                          <Typography variant="caption">{row[col] != null ? String(row[col]) : '—'}</Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      )}

      {selectedDatasetId && columns.length === 0 && (
        <Alert severity="warning">
          This dataset has no column data. Add a version with records first.
        </Alert>
      )}

      {/* Feature list */}
      <Typography variant="subtitle1" fontWeight={600}>
        Custom Features ({features.length})
      </Typography>

      {features.map((cf) => (
        <Paper key={cf.id} variant="outlined" sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={cf.type === 'preset' ? 'Preset' : cf.type === 'expression' ? 'Expr' : 'Python'}
                size="small"
                color={cf.type === 'preset' ? 'primary' : cf.type === 'expression' ? 'secondary' : 'warning'}
                variant="outlined"
              />
              <Typography variant="subtitle2">{cf.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {cf.type === 'preset' && `${cf.transform}(${cf.source_column || '?'})`}
                {cf.type === 'expression' && (cf.formula || '—')}
                {cf.type === 'python' && 'Python function'}
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => { setEditing({ ...cf }); setEditTab(cf.type); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Duplicate">
                <IconButton size="small" onClick={() => duplicate(cf)}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton size="small" color="error" onClick={() => remove(cf.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      ))}

      {/* Add / Edit form */}
      {editing ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle2">
              {features.some((c) => c.id === editing.id) ? 'Edit' : 'New'} Custom Feature
            </Typography>
            <TextField
              label="Feature Name"
              placeholder="e.g. sma_close_20, price_spread, custom_signal"
              value={editing.name}
              onChange={(e) => setEditing((prev) => prev ? { ...prev, name: e.target.value } : prev)}
              size="small"
              fullWidth
              required
            />

            <Tabs
              value={editTab}
              onChange={(_, v) => { setEditTab(v); setEditing((prev) => prev ? { ...prev, type: v } : prev); }}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Preset" value="preset" icon={<FunctionsIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
              <Tab label="Expression" value="expression" icon={<FunctionsIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
              <Tab label="Python" value="python" icon={<CodeIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
            </Tabs>

            {editTab === 'preset' && (
              <Stack spacing={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Transform</InputLabel>
                  <Select
                    value={editing.transform || 'sma'}
                    label="Transform"
                    onChange={(e) => {
                      const t = PRESET_TRANSFORMS.find((p) => p.value === e.target.value);
                      const defaults: Record<string, number> = {};
                      t?.params.forEach((p) => { defaults[p.key] = p.default; });
                      setEditing((prev) => prev ? { ...prev, transform: e.target.value, params: defaults } : prev);
                    }}
                  >
                    {PRESET_TRANSFORMS.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        <Box>
                          <Typography variant="body2">{t.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{t.description}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Autocomplete
                  freeSolo
                  options={columns}
                  value={editing.source_column || ''}
                  onChange={(_, val) => setEditing((prev) => prev ? { ...prev, source_column: (val as string) || '' } : prev)}
                  renderInput={(params) => (
                    <TextField {...params} label="Source Column" placeholder="Select or type column" size="small" />
                  )}
                  size="small"
                />
                {(() => {
                  const t = PRESET_TRANSFORMS.find((p) => p.value === editing.transform);
                  if (!t?.params.length) return null;
                  return (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {t.params.map((p) => (
                        <TextField
                          key={p.key}
                          label={p.label}
                          type="number"
                          size="small"
                          value={editing.params?.[p.key] ?? p.default}
                          onChange={(e) =>
                            setEditing((prev) => prev ? { ...prev, params: { ...prev.params, [p.key]: Number(e.target.value) || 0 } } : prev)
                          }
                          sx={{ width: 120 }}
                        />
                      ))}
                    </Box>
                  );
                })()}
              </Stack>
            )}

            {editTab === 'expression' && (
              <Stack spacing={2}>
                <TextField
                  label="Formula"
                  placeholder="e.g. close - open, high / low, (ask + bid) / 2"
                  helperText={columns.length > 0 ? `Available: ${columns.slice(0, 8).join(', ')}${columns.length > 8 ? '...' : ''}` : ''}
                  value={editing.formula || ''}
                  onChange={(e) => setEditing((prev) => prev ? { ...prev, formula: e.target.value } : prev)}
                  size="small"
                  fullWidth
                />
                <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                  Arithmetic: <code>+</code> <code>-</code> <code>*</code> <code>/</code> and
                  functions: <code>abs()</code>, <code>log()</code>, <code>sqrt()</code>, <code>lag(col, n)</code>, <code>sma(col, n)</code>
                </Alert>
              </Stack>
            )}

            {editTab === 'python' && (
              <Stack spacing={2}>
                <TextField
                  label="Python Code"
                  placeholder={'def compute(df):\n    return df["close"].pct_change()'}
                  helperText="compute(df) receives the DataFrame, must return a pandas Series"
                  value={editing.code || ''}
                  onChange={(e) => setEditing((prev) => prev ? { ...prev, code: e.target.value } : prev)}
                  multiline
                  rows={6}
                  fullWidth
                  slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: '0.85rem' } } }}
                />
                <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                  Sandboxed: only <code>pandas</code>, <code>numpy</code>, <code>math</code> available.
                </Alert>
              </Stack>
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => setEditing(null)}>Cancel</Button>
              <Button
                size="small"
                variant="contained"
                onClick={save}
                disabled={!editing.name.trim()}
              >
                {features.some((c) => c.id === editing.id) ? 'Update' : 'Add'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={startNew}
          >
            Add Custom Feature
          </Button>
          <Button
            startIcon={<TemplateIcon />}
            variant="outlined"
            color="secondary"
            onClick={() => setShowTemplates((p) => !p)}
          >
            {showTemplates ? 'Hide Templates' : 'Browse Templates'}
          </Button>
        </Box>
      )}

      {/* Inline template browser */}
      <Collapse in={showTemplates && !editing}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TemplateIcon fontSize="small" /> Feature Templates
          </Typography>
          <Tabs
            value={builderTemplateCat}
            onChange={(_, v) => setBuilderTemplateCat(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}
          >
            {TEMPLATE_CATEGORIES.map((cat) => (
              <Tab key={cat} label={cat} value={cat} sx={{ minHeight: 36, py: 0 }} />
            ))}
          </Tabs>
          <Stack spacing={1}>
            {FEATURE_TEMPLATES.filter((t) => t.category === builderTemplateCat).map((tpl, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => {
                  const newFeatures = tpl.features.map((f) => ({ ...f, id: nextCfId() }));
                  setFeatures((prev) => [...prev, ...newFeatures]);
                  toast.success(`Added ${newFeatures.length} feature${newFeatures.length > 1 ? 's' : ''} from "${tpl.label}"`);
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{tpl.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{tpl.description}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {tpl.features.map((f, fi) => (
                        <Chip key={fi} label={f.name} size="small" variant="outlined"
                          color={f.type === 'preset' ? 'primary' : f.type === 'expression' ? 'secondary' : 'warning'} />
                      ))}
                    </Box>
                  </Box>
                  <Button size="small" variant="text">+ Add</Button>
                </Box>
              </Paper>
            ))}
          </Stack>
        </Paper>
      </Collapse>
    </Stack>
  );
}
